import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Footer from './Footer';

interface LayoutProps {
  children: React.ReactNode;
}

const Header: React.FC = () => {
  const location = useLocation();
  const { currentUser } = useAuth();
  const isLoginPage = location.pathname === '/login';
  const isRegisterPage = location.pathname === '/register';
  const isResetPasswordPage = location.pathname === '/reset-password';
  const isAuthPage = isLoginPage || isRegisterPage || isResetPasswordPage;
  const isStaging = process.env.REACT_APP_ENVIRONMENT === 'staging';
  
  // Check if user is on an authenticated route (dashboard, admin, etc.)
  const isAuthenticatedRoute = location.pathname.startsWith('/dashboard') || 
                             location.pathname.startsWith('/admin') ||
                             location.pathname.startsWith('/add-trading-account') || 
                             location.pathname.startsWith('/account-details');
  
  // Don't show this header if user is logged in and on an authenticated route
  // (these routes have their own headers)
  if (currentUser && isAuthenticatedRoute) {
    return null;
  }
  
  return (
    <nav className="bg-black">
      {isStaging && (
        <div className="bg-amber-500 text-black text-center py-1 font-medium">
          ⚠️ STAGING ENVIRONMENT - FOR TESTING ONLY ⚠️
        </div>
      )}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16">
          <div className="flex">
            <div className="flex-shrink-0 flex items-center">
              <Link to="/" className="flex items-center hover:opacity-90 transition-opacity duration-200">
                <img src="/T1Logo180.png" alt="T1 Logo" className="h-8 w-auto" />
                <div className="ml-3 flex items-center">
                  <span className="text-xl font-bold text-white">T1 FX TEAM</span>
                  {isStaging && (
                    <span className="ml-2 bg-amber-500 text-black text-xs px-2 py-0.5 rounded-full font-bold">
                      STAGING
                    </span>
                  )}
                </div>
              </Link>
            </div>
          </div>
          <div className="flex items-center space-x-4">
            {isAuthPage && (
              <Link 
                to="/" 
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-black bg-white hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-white"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" viewBox="0 0 20 20" fill="currentColor">
                  <path d="M10.707 2.293a1 1 0 00-1.414 0l-7 7a1 1 0 001.414 1.414L4 10.414V17a1 1 0 001 1h2a1 1 0 001-1v-2a1 1 0 011-1h2a1 1 0 011 1v2a1 1 0 001 1h2a1 1 0 001-1v-6.586l.293.293a1 1 0 001.414-1.414l-7-7z" />
                </svg>
                Home
              </Link>
            )}
            {!isLoginPage && !currentUser && (
              <Link 
                to="/login" 
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-t1blue hover:bg-t1blue-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-t1blue"
              >
                Sign In
              </Link>
            )}
            {!isRegisterPage && !currentUser && (
              <Link 
                to="/register" 
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-black bg-white hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-white"
              >
                Register
              </Link>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
};

const Layout: React.FC<LayoutProps> = ({ children }) => {
  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-grow">
        {children}
      </main>
      <Footer />
    </div>
  );
};

export default Layout; 